import { DirectionsRenderer, DirectionsService, GoogleMap, InfoWindowF, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { images } from "../../utils/images";
import FuelMapCard from "./FuelMapCard";

const mapOptions = {
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    styles: [
        {
            featureType: "poi",
            stylers: [{ visibility: "off" }],
        },
    ],
};

const containerStyle = {
    width: "100%",
    height: "400px"
};

const FuelMap = () => {
    const [hoverMarkerId, setHoveredMarkerId] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 30.3753, lng: 69.3451 });
    const [mapZoom, setMapZoom] = useState(5);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: (process.env.REACT_APP_API_GOOGLE_MAP_KEY || "").trim()
    })

    const { getFuelLogDetailsData, fuelGeneralDetailsData } = useSelector((state) => state.fuel)
    const [directionsResponse, setDirectionsResponse] = useState(null);

    const startPoint = useMemo(() => (getFuelLogDetailsData?.dispatch_latitude && getFuelLogDetailsData?.dispatch_longitude) ? {
        lat: Number(getFuelLogDetailsData?.dispatch_latitude),
        lng: Number(getFuelLogDetailsData?.dispatch_longitude)
    } : null, [getFuelLogDetailsData]);

    const endPoint = useMemo(() => (getFuelLogDetailsData?.decanting_latitude && getFuelLogDetailsData?.decanting_longitude) ? {
        lat: Number(getFuelLogDetailsData?.decanting_latitude),
        lng: Number(getFuelLogDetailsData?.decanting_longitude)
    } : null, [getFuelLogDetailsData]);

    const fuelTimelineMarkers = useMemo(() => {
        return (fuelGeneralDetailsData || []).map(event => ({
            ...event,
            lat: Number(event?.fuel_station?.lat),
            lng: Number(event?.fuel_station?.lng)
        })).filter(event => !isNaN(event.lat) && !isNaN(event.lng));
    }, [fuelGeneralDetailsData]);

    const [map, setMap] = useState(null);

    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const onIdle = useCallback(() => {
        if (map) {
            const newCenter = map.getCenter();
            if (newCenter) {
                const lat = newCenter.lat();
                const lng = newCenter.lng();
                setMapCenter({ lat, lng });
            }
            const newZoom = map.getZoom();
            setMapZoom(newZoom);
        }
    }, [map]);

    const directionsCallback = useCallback((result, status) => {
        if (result !== null) {
            if (status === 'OK') {
                setDirectionsResponse(result);
            } else {
                console.log('Directions request failed due to ' + status);
            }
        }
    }, []);

    useEffect(() => {
        setDirectionsResponse(null);
    }, [startPoint, endPoint, fuelTimelineMarkers]);

    useEffect(() => {
        if (startPoint) {
            setMapCenter(startPoint);
        }
    }, [startPoint]);

    if (!isLoaded) return <div>Loading Map...</div>

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={mapZoom}
            options={mapOptions}
            onLoad={onLoad}
            onIdle={onIdle}
        >
            {startPoint && endPoint && !directionsResponse && (
                <DirectionsService
                    options={{
                        destination: endPoint,
                        origin: startPoint,
                        waypoints: fuelTimelineMarkers.map(marker => ({
                            location: { lat: marker.lat, lng: marker.lng },
                            stopover: true
                        })),
                        travelMode: 'DRIVING'
                    }}
                    callback={directionsCallback}
                />
            )}

            {directionsResponse && (
                <DirectionsRenderer
                    options={{
                        directions: directionsResponse,
                        suppressMarkers: true,
                        preserveViewport: true,
                        polylineOptions: {
                            strokeColor: "#2563EB",
                            strokeOpacity: 0.8,
                            strokeWeight: 4,
                        }
                    }}
                />
            )}
            {startPoint && (
                <MarkerF
                    position={startPoint}
                    zIndex={1002}
                    icon={{
                        url: images.startMap,
                        scaledSize: new window.google.maps.Size(30, 30)
                    }}
                />
            )}
            {endPoint && (
                <MarkerF
                    position={endPoint}
                    zIndex={1002}
                    icon={{
                        url: images.trip_end,
                        scaledSize: new window.google.maps.Size(30, 30)
                    }}
                />
            )}

            {fuelTimelineMarkers.map((event) => {
                return (
                    <MarkerF
                        key={event.id}
                        position={{ lat: event.lat, lng: event.lng }}
                        zIndex={1100}
                        onMouseOver={() => setHoveredMarkerId(event.id)}
                        onMouseOut={() => setHoveredMarkerId(null)}
                        icon={{
                            url: images.refuelImg,
                            scaledSize: new window.google.maps.Size(20, 20)
                        }}
                    >
                        {(hoverMarkerId === event.id) && (
                            <InfoWindowF
                                position={{ lat: event.lat, lng: event.lng }}
                                onCloseClick={() => { }}
                                options={{
                                    disableAutoPan: true,
                                    pixelOffset: new window.google.maps.Size(0, -10),
                                }}
                            >
                                <div style={{ minWidth: '150px' }}>
                                    <FuelMapCard event={event} />
                                </div>
                            </InfoWindowF>
                        )}
                    </MarkerF>
                )
            })}

        </GoogleMap>
    );
};

export default FuelMap;
